import React, { useState, useRef, useEffect } from 'react';
import SubframeCore from '@subframe/core';

interface VoiceMessageProps {
  audioUrl: string;
  duration?: number;
  isPlaying?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  className?: string;
  showVolumeControls?: boolean;
}

const VoiceMessage: React.FC<VoiceMessageProps> = ({
  audioUrl,
  duration = 0,
  isPlaying: externalIsPlaying,
  onPlay,
  onPause,
  onEnded,
  className = '',
  showVolumeControls = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number>();

  // Sync with external playing state if provided
  useEffect(() => {
    if (externalIsPlaying !== undefined) {
      setIsPlaying(externalIsPlaying);
      if (externalIsPlaying) {
        audioRef.current?.play();
      } else {
        audioRef.current?.pause();
      }
    }
  }, [externalIsPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      const currentDuration = audio.duration;
      if (isFinite(currentDuration) && currentDuration > 0) {
        const currentProgress = (audio.currentTime / currentDuration) * 100;
        setProgress(currentProgress);
        setCurrentTime(audio.currentTime);
      }
      animationRef.current = requestAnimationFrame(updateProgress);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      animationRef.current = requestAnimationFrame(updateProgress);
      onPlay?.();
    };

    const handlePause = () => {
      setIsPlaying(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      onPause?.();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      onEnded?.();
    };

    const handleLoadedMetadata = () => {
      if (isFinite(audio.duration)) {
        setTotalDuration(audio.duration);
        setIsAudioLoaded(true);
      }
    };

    const handleLoadedData = () => {
      if (isFinite(audio.duration)) {
        setTotalDuration(audio.duration);
        setIsAudioLoaded(true);
      }
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('loadeddata', handleLoadedData);
    
    // Try to get duration directly if already loaded
    if (audio.readyState >= 1 && isFinite(audio.duration)) {
      setTotalDuration(audio.duration);
      setIsAudioLoaded(true);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [onPlay, onPause, onEnded]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !isAudioLoaded) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    
    const newTime = clickPosition * (audioRef.current.duration);
    audioRef.current.currentTime = newTime;
    setProgress(clickPosition * 100);
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 1;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  return (
    <div className={`p-4 rounded-md ${className}`}>
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        preload="metadata" 
        onLoadedMetadata={(e) => {
          if (e.currentTarget && isFinite(e.currentTarget.duration)) {
            setTotalDuration(e.currentTarget.duration);
            setIsAudioLoaded(true);
          }
        }}
      />
      
      <div className="flex justify-center mb-4">
        <button 
          className="w-16 h-16 flex items-center justify-center rounded-full bg-blue-500 text-white cursor-pointer"
          onClick={togglePlayPause}
          aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
        >
          {isPlaying ? (
            <SubframeCore.Icon name="FeatherPause" className="text-heading-1 font-heading-1" />
          ) : (
            <SubframeCore.Icon name="FeatherPlay" className="text-heading-1 font-heading-1 ml-1" />
          )}
        </button>
      </div>
      
      <div className="flex items-center justify-center mb-2 px-4">
        <span className="text-neutral-700 text-sm">
          {formatTime(currentTime)}
        </span>
        <div className="mx-2 grow">
          <div 
            className="w-full h-1 bg-gray-200 rounded-full cursor-pointer"
            onClick={handleProgressBarClick}
          >
            <div 
              className="h-full bg-blue-500 rounded-full" 
              style={{ width: `${progress}%` }} 
            />
          </div>
        </div>
        <span className="text-neutral-700 text-sm">
          {isAudioLoaded ? formatTime(totalDuration) : '0:00'}
        </span>
      </div>
      
      {showVolumeControls && (
        <div className="flex items-center justify-center mt-2">
          <button 
            onClick={toggleMute}
            className="text-neutral-600 hover:text-blue-500 mr-2"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <SubframeCore.Icon name="FeatherVolumeX" className="text-body font-body" />
            ) : volume < 0.5 ? (
              <SubframeCore.Icon name="FeatherVolume1" className="text-body font-body" />
            ) : (
              <SubframeCore.Icon name="FeatherVolume2" className="text-body font-body" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-48 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            aria-label="Volume control"
          />
        </div>
      )}
    </div>
  );
};

export default VoiceMessage;